/**
 * A conversa da tela com a API.
 *
 * Três coisas moram aqui, e nenhuma delas é regra de negócio:
 *
 *   - o CSRF, que a portaria exige em toda escrita (cookie legível + cabeçalho);
 *   - o erro em português, que já vem pronto do servidor e é repassado como está;
 *   - o canal de eventos, UM só para o aplicativo inteiro. O navegador limita
 *     conexões por origem: três telas com EventSource próprio estouram o limite
 *     e a última fica muda sem avisar.
 */

const csrf = () =>
  document.cookie
    .split("; ")
    .find((c) => c.startsWith("crm_csrf="))
    ?.slice(9) ?? "";

/*
 * A frase para quando o corpo NÃO é o envelope da casa.
 *
 * Recusa do CRM já vem em português por `erro()` (src/rotas/comum.ts) e é
 * repassada como está. Fora disso o que chega é o padrão do Hono em texto cru —
 * "Internal Server Error" numa exceção, "404 Not Found" num caminho errado — ou
 * corpo vazio de proxy e rede. Isso ia inteiro para o balão do rodapé, em
 * inglês, sem dizer o que fazer. Não prometemos que nada foi gravado: um 500
 * pode acontecer depois da escrita, e essa é justamente a hora de conferir.
 */
const semRecado = (status) =>
  `O servidor não respondeu direito (${status}). Confira antes de repetir; se continuar, chame quem cuida do servidor.`;

/*
 * O prazo de toda leitura.
 *
 * `fetch` não desiste sozinho: conexão pendurada — VPN caindo no meio, VM de
 * 1 OCPU ocupada, servidor derrubado sem fechar o soquete — deixa a promessa em
 * voo para sempre, e a tela que espera por ela fica "Carregando…" até alguém
 * recarregar a página. É esse o "carregamento sem fim": não é a tela que erra,
 * é o pedido que nunca termina. 25s é folga larga para a rede privada do
 * escritório e ainda assim um FIM.
 *
 * Só nas leituras. Escrita cortada pela metade não desfaz o que o servidor já
 * gravou — abortar um POST daria "não enviado" sobre uma mensagem enviada.
 */
const PRAZO_MS = 25_000;

/*
 * O que depende de MODELO tem outro prazo — e não é folga, é a natureza da rota.
 *
 * 25s é generoso para o CRM responder do próprio banco e apertado para uma
 * resposta que passa por um modelo: o painel do copiloto lê o caso e pergunta ao
 * assistente, e o log do servidor registra essa rota em 30s com frequência.
 * Cortada em 25, a tela dizia "o servidor não respondeu — pode estar fora do ar"
 * sobre um servidor que estava trabalhando e responderia cinco segundos depois.
 *
 * Três minutos porque é mais que o teto do lado de lá: o cliente ACP desiste de
 * um pedido ao agente em 120s (`chamar` em `src/acp.ts`). Quem tem de falhar
 * primeiro é quem sabe o motivo — aí a tela mostra a recusa do servidor em
 * português em vez de um prazo estourado sem explicação.
 */
const PRAZO_DE_IA_MS = 180_000;

/* Prefixo de rota que fala com modelo, agente ou sistema de terceiro. */
const LENTAS = ["/api/copiloto", "/api/agents", "/api/astrea", "/api/proxy"];

const prazoDe = (caminho) => (LENTAS.some((p) => String(caminho).startsWith(p)) ? PRAZO_DE_IA_MS : PRAZO_MS);

const comPrazo = (sinal, caminho) => {
  const prazo = AbortSignal.timeout(prazoDe(caminho));
  if (!sinal) return prazo;
  /* Junta com o corte de quem chamou (busca redigitada, tela trocada): quem
     abortar primeiro manda, e o motivo dele é o que chega aqui. Navegador sem
     `AbortSignal.any` fica com o corte de quem chamou e sem prazo — perder o
     prazo numa lista é bem menos grave que derrubar a mesa por um TypeError. */
  return AbortSignal.any ? AbortSignal.any([sinal, prazo]) : sinal;
};

/*
 * A falha ANTES de haver resposta — rede fora, prazo estourado, servidor mudo.
 *
 * `fetch` rejeita com "Failed to fetch"/"signal timed out": inglês, sem dizer o
 * que fazer, e indistinguível de recusa do servidor. `status: 0` é o que a tela
 * lê para separar "não chegou" de "chegou e recusou" — são duas telas
 * diferentes e dois caminhos de volta diferentes.
 */
const semResposta = (e, caminho) => {
  /* Corte nosso não é falha: sobe como está para `usePaginacao` reconhecê-lo
     pelo nome e descartar a rodada em silêncio. */
  if (e?.name === "AbortError") throw e;
  const nova = new Error(
    e?.name === "TimeoutError"
      ? /* O prazo dito é o que a rota tinha de verdade: dizer "25s" numa rota que
           esperou três minutos manda a pessoa procurar problema de rede. */
        `O servidor não respondeu em ${prazoDe(caminho) / 1000}s. Ele pode estar fora do ar, ou a rede do escritório caiu no meio.`
      : "Não deu para falar com o servidor. Confira a conexão com a rede do escritório e tente de novo.",
  );
  nova.status = 0;
  throw nova;
};

/*
 * Quem quer saber que a sessão caiu no meio do caminho. Um só: a mesa.
 *
 * 401 depois de a pessoa já estar dentro não é erro de uma tela — é a sessão
 * que terminou, e TODA tela dali para a frente vai falhar igual. Sem este
 * aviso, cada pedido em voo virava um balão de seis segundos sobre uma mesa que
 * não ia mais carregar nada, e ninguém era mandado de volta para a porta.
 */
let aoCairASessao = null;
export function seASessaoCair(fn) {
  aoCairASessao = fn;
  return () => {
    if (aoCairASessao === fn) aoCairASessao = null;
  };
}

async function ler(r) {
  const texto = await r.text();
  let corpo = null;
  try {
    corpo = texto ? JSON.parse(texto) : null;
  } catch {
    /* Texto cru não é recado da casa: fica de fora e a frase acima entra. */
    corpo = null;
  }
  if (!r.ok) {
    /* `daCasa` diz se a frase é do CRM (`{erro}` em português, escrita para
       quem lê) ou a nossa de emergência. A tela usa a do servidor quando existe
       — "Atendimento não encontrado" é dele, não nossa. */
    const daCasa = typeof corpo?.erro === "string" && Boolean(corpo.erro);
    const e = new Error(daCasa ? corpo.erro : semRecado(r.status));
    e.status = r.status;
    e.daCasa = daCasa;
    e.permissao = corpo?.permissao;
    if (r.status === 401) aoCairASessao?.();
    throw e;
  }
  return corpo;
}

export const pegar = (caminho, sinal) =>
  fetch(caminho, { credentials: "same-origin", signal: comPrazo(sinal, caminho) }).then(ler, (e) => semResposta(e, caminho));

/**
 * O mesmo GET, mais quantos existem no total.
 *
 * Lista longa vem em pedaço, e o pedaço sozinho não diz se acabou. O total vem
 * em `x-crm-total` (posto por `total()` em `src/rotas/entrada.ts`) e não dentro
 * do corpo: as rotas agregadas devolvem várias listas no mesmo JSON, e enfiar
 * `{itens,total}` em cada uma mudava a forma de resposta que a mesa inteira já
 * lê. Cabeçalho não mexe em nada que já existe.
 */
export async function pegarComTotal(caminho, sinal) {
  const r = await fetch(caminho, { credentials: "same-origin", signal: comPrazo(sinal, caminho) }).catch((e) =>
    semResposta(e, caminho),
  );
  const bruto = r.headers.get("x-crm-total");
  return { corpo: await ler(r), total: bruto == null ? null : Number(bruto) };
}

/** Monta `?a=1&b=2` deixando de fora o que está vazio, nulo ou indefinido. */
export const consulta = (campos) => {
  const busca = new URLSearchParams(
    Object.entries(campos).filter(([, v]) => v !== "" && v !== null && v !== undefined && v !== false),
  );
  const texto = busca.toString();
  return texto ? `?${texto}` : "";
};

export const mandar = (caminho, corpo, metodo = "POST") =>
  fetch(caminho, {
    method: metodo,
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-crm-csrf": csrf() },
    /* GET não leva corpo: o navegador recusa a requisição inteira ("Request with
       GET/HEAD method cannot have body") — e quem chama `mandar` com "GET" quer
       só a query. */
    ...(metodo === "GET" ? {} : { body: JSON.stringify(corpo ?? {}) }),
  }).then(ler);

export const enviarArquivo = (caminho, form) =>
  fetch(caminho, { method: "POST", credentials: "same-origin", headers: { "x-crm-csrf": csrf() }, body: form }).then(ler);

/**
 * O mesmo envio de arquivo, com o quanto já subiu.
 *
 * `fetch` não conta byte enviado — não existe evento de progresso de upload
 * nele. É por isso que este é o único lugar da tela com `XMLHttpRequest`: o
 * cliente manda o vídeo de 40 MB da audiência e, sem a barra, a única diferença
 * entre "subindo" e "travou" era a paciência de quem espera. Erro e recado saem
 * pelo MESMO caminho do resto (`ler`), com um envelope falso de resposta.
 */
export function enviarArquivoComProgresso(caminho, form, aoProgresso) {
  return new Promise((aceitar, recusar) => {
    const pedido = new XMLHttpRequest();
    pedido.open("POST", caminho, true);
    pedido.withCredentials = true;
    pedido.setRequestHeader("x-crm-csrf", csrf());
    pedido.upload.onprogress = (e) => {
      /* Sem `lengthComputable` não há fração honesta: melhor deixar a barra
         indeterminada (o chamador recebe `null`) que desenhar 0% eterno. */
      aoProgresso?.(e.lengthComputable && e.total > 0 ? e.loaded / e.total : null);
    };
    /* O servidor já recebeu tudo e está gravando: 100% e a barra vira "gravando". */
    pedido.upload.onload = () => aoProgresso?.(1);
    pedido.onload = () =>
      ler({ ok: pedido.status >= 200 && pedido.status < 300, status: pedido.status, text: async () => pedido.responseText }).then(
        aceitar,
        recusar,
      );
    /* `semResposta` LANÇA (é o formato do `.then` do fetch): chamada direto em
       `recusar(...)`, a exceção estourava dentro do ouvinte e a promessa nunca
       terminava — rede caindo no meio do envio deixava "enviando…" para sempre. */
    pedido.onerror = () => {
      try {
        semResposta({ name: "TypeError" }, caminho);
      } catch (e) {
        recusar(e);
      }
    };
    pedido.onabort = () => {
      const e = new Error("Envio cancelado.");
      e.name = "AbortError";
      recusar(e);
    };
    pedido.send(form);
    /* Quem chamou pode desistir: devolve o cancelador junto da promessa. */
    aoProgresso?.(0, () => pedido.abort());
  });
}

/* ------------------------------------------------------------- eventos */

const ouvintes = new Set();
let canal = null;

/**
 * Assina o canal de eventos. A primeira assinatura abre a conexão; as demais
 * pegam carona — é o "um canal só" que a casa exige.
 */
export function aoEvento(fn) {
  ouvintes.add(fn);
  if (!canal) {
    canal = new EventSource("/api/stream", { withCredentials: true });
    canal.onmessage = (e) => {
      let dado;
      try {
        dado = JSON.parse(e.data);
      } catch {
        return;
      }
      for (const o of ouvintes) o(dado);
    };
    /* Queda de rede: o EventSource já reconecta sozinho; o que não pode é a
       tela achar que continua ao vivo. O erro vira um evento interno. */
    canal.onerror = () => {
      for (const o of ouvintes) o({ tipo: "canal", acao: "instavel" });
    };
    /* E a volta também é notícia: `onopen` dispara na reconexão, então é ele que
       apaga o aviso. Sem isso a faixa "reconectando" ficaria para sempre, o que
       é a mesma mentira ao contrário. */
    canal.onopen = () => {
      for (const o of ouvintes) o({ tipo: "canal", acao: "ok" });
    };
  }
  return () => ouvintes.delete(fn);
}

/* -------------------------------------------------------------- sessão */

export const eu = () => pegar("/api/auth/eu");
export const entrar = (email, senha) => mandar("/api/auth/login", { email, senha });
/* Esqueci a senha: o link vai para o e-mail, e a página volta com `?senha=`. */
export const pedirLinkDeSenha = (email) => mandar("/api/auth/esqueci", { email });
export const conferirLinkDeSenha = (token) => pegar(`/api/auth/link-de-senha${consulta({ token })}`);
export const gravarSenhaPeloLink = (token, nova) => mandar("/api/auth/redefinir", { token, nova });
export const sair = () => mandar("/api/auth/logout", {});
/* Trocar a própria senha: a atual é conferida no servidor e as OUTRAS sessões
   caem — a desta aba fica, para a pessoa seguir de onde estava. */
export const trocarSenha = (atual, nova) => mandar("/api/auth/senha", { atual, nova });
/* O que falta para quem acabou de chegar, lido do banco do setor. */
export const primeirosPassos = () => pegar("/api/primeiros-passos");

/**
 * Em que escritório esta aba fica.
 *
 * O escritório corrente é COOKIE (`crm_workspace`), então isto não devolve dado
 * para a tela guardar: o que muda é a origem de tudo que vier depois. Quem chama
 * recarrega a mesa — ver `trocarEscritorio` em `estado.jsx`.
 */
export const trocarWorkspace = (chave) => mandar("/api/workspaces/ativo", { chave });

/*
 * Gerenciar escritório é do banco de CONTROLE, não do escritório aberto: a
 * portaria mantém `/api/workspaces` fora do contexto de propósito. Por isso
 * criar, renomear e dar acesso NÃO recarregam a mesa — nada do caso do cliente
 * muda com eles.
 */
export const workspaces = () => pegar("/api/workspaces");
export const criarWorkspace = (b) => mandar("/api/workspaces", b);
export const ajustarWorkspace = (chave, patch) => mandar(`/api/workspaces/${encodeURIComponent(chave)}`, patch, "PATCH");
export const darAcessoAoWorkspace = (chave, usuario_id) =>
  mandar(`/api/workspaces/${encodeURIComponent(chave)}/membros`, { usuario_id });
export const tirarAcessoAoWorkspace = (chave, usuario_id) =>
  mandar(`/api/workspaces/${encodeURIComponent(chave)}/membros/${usuario_id}`, {}, "DELETE");

/* --------------------------------------------------------------- mesa */

export const mesa = () => pegar("/api/mesa");
/** Até três respostas curtas para a última mensagem do cliente (`[]` quando não há). */
export const respostasCurtas = (id) => pegar(`/api/mesa/${id}/respostas-curtas`);

/*
 * O tamanho da página da mesa.
 *
 * 200 de propósito: é exatamente o teto que existia antes (`TETO_DA_MESA`), e
 * era ele que os seletores de "escolher atendimento" (transferir, encaminhar,
 * cliente da gravação) já recebiam. Manter o mesmo número na PRIMEIRA página é
 * o que garante que nada regrediu para eles — o que a rolagem trouxe de novo é
 * o que ninguém tinha: do 201 ao último caso da planilha.
 *
 * ponytail: página menor (60) pinta a lista mais rápido, mas aí os seletores
 * passam a ver 60 casos e precisam de busca no servidor. Quando a primeira
 * pintura doer, esse é o caminho.
 */
export const TAMANHO_DA_MESA = 200;

/** Uma página da mesa, com o recorte e o total que o servidor calculou. */
export const mesaPagina = ({
  offset = 0,
  limite = TAMANHO_DA_MESA,
  q = "",
  filtro = "",
  etapa = "",
  resp = "",
  tag = "",
  grupo = "",
  criativo = "",
  numero = "",
  sinal,
} = {}) => pegarComTotal(`/api/mesa${consulta({ offset, limite, q, filtro, etapa, resp, tag, grupo, criativo, numero })}`, sinal);
/**
 * As chaves do recorte INTEIRO — o "selecionar todos" da mesa.
 *
 * A lista carrega 200 por vez; marcar o que está desenhado não é "todos" quando
 * o filtro alcança 528. O servidor devolve só os ids, e a tela marca com eles.
 */
export const idsDaMesa = ({
  q = "",
  filtro = "",
  etapa = "",
  resp = "",
  tag = "",
  grupo = "",
  criativo = "",
  numero = "",
} = {}) => pegar(`/api/mesa/ids${consulta({ q, filtro, etapa, resp, tag, grupo, criativo, numero })}`);

/*
 * As ações em LOTE (`src/lote.ts`): uma chamada, muitos atendimentos.
 *
 * Uma função por ação porque é uma ROTA por ação — a permissão de arquivar não é
 * a de apagar, e o servidor cobra cada uma na porta dela. Todas devolvem
 * `{ feitos, falhas: [{ id, nome, erro }] }`: em lote, "deu erro" não serve —
 * quem apertou precisa saber QUAIS não foram.
 */
export const loteArquivar = (ids) => mandar("/api/mesa/lote/arquivar", { ids });
export const loteApagar = (ids) => mandar("/api/mesa/lote/apagar", { ids });
export const loteEtapa = (ids, etapa) => mandar("/api/mesa/lote/etapa", { ids, etapa });
export const loteEtiqueta = (ids, tag, tirar = false) => mandar("/api/mesa/lote/etiqueta", { ids, tag, tirar });
export const loteResponsaveis = (ids, pessoas) => mandar("/api/mesa/lote/responsaveis", { ids, pessoas });

export const caso = (id) => pegar(`/api/mesa/${id}`);
export const marcarLida = (id) => mandar(`/api/mesa/${id}/lida`, {});
/* "Volto nisso": devolve a ÚLTIMA de entrada para não lida. Quem marca sai da
   conversa — a tela marca lida ao abrir, e ficar aqui desfaria o gesto. */
export const marcarNaoLida = (id) => mandar(`/api/mesa/${id}/nao-lida`, {});
/* Quantas mensagens `GET /api/mesa/:id` traz. A tela precisa do número para
   saber se o que chegou é a conversa inteira ou só o fim dela. */
export const TAMANHO_DA_CONVERSA = 50;

/** O pedaço ANTERIOR da conversa: `antes` é o id da mais antiga que já temos. */
export const conversaAntes = (id, antes, limite = 60) => pegar(`/api/leads/${id}/conversa${consulta({ antes, limite })}`);

export const enviarMensagem = (id, texto, extra = {}) => mandar(`/api/leads/${id}/mensagem`, { texto, ...extra });
/** Junta as fotos escolhidas num PDF só, na pasta do cliente. */
export const juntarEmPdf = (id, mensagens, titulo) => mandar(`/api/leads/${id}/juntar-pdf`, { mensagens, titulo });

/** Mensagens escritas agora para sair depois — o relógio do CRM segura. */
export const agendadas = (id) => pegar(`/api/leads/${id}/agendadas`);
export const agendarMensagem = (id, texto, quando) => mandar(`/api/leads/${id}/agendar`, { texto, quando });
export const cancelarAgendada = (id, agendada) => mandar(`/api/leads/${id}/agendadas/${agendada}`, undefined, "DELETE");
export const enviarAgendadaAgora = (id, agendada) => mandar(`/api/leads/${id}/agendadas/${agendada}/enviar`, {});

/** Enquete: pergunta com opções, votada dentro do WhatsApp do cliente. */
export const enviarEnquete = (id, pergunta, opcoes, multipla = false) =>
  mandar(`/api/leads/${id}/enquete`, { pergunta, opcoes, multipla });
export const anexar = (id, arquivo, { como, legenda, responde_a } = {}) => {
  const form = new FormData();
  form.append("arquivo", arquivo);
  /* `como` diz ao provedor o tipo da mídia: recado de voz mandado como
     documento chega sem player no celular do cliente. */
  if (como) form.append("como", como);
  if (legenda) form.append("legenda", legenda);
  if (responde_a) form.append("responde_a", responde_a);
  return enviarArquivo(`/api/leads/${id}/anexo`, form);
};

/** O mesmo anexo, dizendo quanto já subiu — é o que a barra do composer lê. */
export const anexarComProgresso = (id, arquivo, { como, legenda, responde_a } = {}, aoProgresso) => {
  const form = new FormData();
  form.append("arquivo", arquivo);
  if (como) form.append("como", como);
  if (legenda) form.append("legenda", legenda);
  if (responde_a) form.append("responde_a", responde_a);
  return enviarArquivoComProgresso(`/api/leads/${id}/anexo`, form, aoProgresso);
};
export const anotar = (id, texto) => mandar(`/api/records/${id}/notes`, { texto });

/* Quem acompanha o atendimento além do titular (`src/responsaveis.ts`). O
   titular continua sendo `dados.colaborador`, gravado por `editarFicha`. */
export const incluirResponsavel = (id, pessoa) => mandar(`/api/leads/${id}/responsaveis`, { pessoa });
export const removerResponsavel = (id, pessoa) =>
  mandar(`/api/leads/${id}/responsaveis/${encodeURIComponent(pessoa)}`, undefined, "DELETE");
/* QUEM CUIDA do caso, o conjunto inteiro — `PUT` porque SUBSTITUI. `POST` acima
   acrescenta um acompanhante; este define quem são os titulares, e o primeiro da
   lista é quem a régua cobra (`src/responsaveis.ts` explica). */
export const definirResponsaveis = (id, pessoas) => mandar(`/api/leads/${id}/responsaveis`, { pessoas }, "PUT");
export const mover = (id, stage, motivo) => mandar(`/api/records/${id}/stage`, { stage, motivo });
export const editarFicha = (id, dados) => mandar(`/api/records/${id}`, { dados }, "PATCH");
/** Nome e telefone do contato — a mesma rota da ficha, outros campos. */
export const editarRegistro = (id, mudanca) => mandar(`/api/records/${id}`, mudanca, "PATCH");
/** Tira o atendimento da mesa sem perder nada — dá para trazer de volta. */
export const arquivarRegistro = (id) => mandar(`/api/records/${id}/arquivar`, {});
/** A volta do arquivo — a outra metade de "dá para trazer de volta". */
export const desarquivarRegistro = (id) => mandar(`/api/records/${id}/desarquivar`, {});
/** Apaga de vez. O servidor recusa caso com peça, contrato assinado ou processo. */
export const apagarRegistro = (id, motivo) =>
  mandar(`/api/records/${id}${motivo ? `?motivo=${encodeURIComponent(motivo)}` : ""}`, undefined, "DELETE");

/* ----------------------------------------------------------- copiloto */

/**
 * A leitura do caso. `renovar` fura a memória do copiloto — é o botão "reler"
 * do cartão, a única forma de pedir uma leitura nova sem a conversa ter mudado.
 * `modelo: "memoria"` só devolve o que já foi lido — é a releitura por evento.
 */
export const copiloto = (id, { modelo = true, renovar = false } = {}) => {
  const q = new URLSearchParams();
  if (!modelo) q.set("modelo", "0");
  else if (modelo === "memoria") q.set("modelo", "memoria");
  if (renovar) q.set("renovar", "1");
  const busca = q.toString();
  return pegar(`/api/copiloto/${id}${busca ? `?${busca}` : ""}`);
};
export const sugerir = (id, variacao) => mandar(`/api/copiloto/${id}/sugerir`, { variacao });
export const extrairFatos = (id) => mandar(`/api/copiloto/${id}/fatos`, {});

/** Conferir um documento do checklist — é o que a régua de cobrança lê depois. */
export const conferirDocumento = (item, estado, observacao) =>
  mandar(`/api/checklist/item/${item}/conferir`, { estado, observacao });
export const retomar = (id) => mandar(`/api/copiloto/${id}/retomar`, {});
export const motor = () => pegar("/api/copiloto/motor");

/* O Astrea está de pé? Fala com o Astrea a cada chamada — está na lista das
   rotas LENTAS, e é por isso que a tela nunca chama isto sozinha em laço. */
export const astreaSaude = (sinal) => pegar("/api/astrea/saude", sinal);

export const fluxos = () => pegar("/api/fluxos");
export const ajustarFluxo = (id, mudanca) => mandar(`/api/fluxos/${id}`, mudanca, "PATCH");
export const criarFluxo = (nome, glifo) => mandar("/api/fluxos", { nome, glifo });
export const apagarFluxo = (id) => mandar(`/api/fluxos/${id}`, {}, "DELETE");
/* O ensaio do fluxo: o que sairia, sem sair. Não grava e não envia. */
export const ensaiarFluxo = (id) => pegar(`/api/fluxos/${id}/ensaio`);

/* ------------------------------------------------------------- provas */

export const anotarProva = (corpo) => mandar("/api/provas", corpo);
export const marcarProva = (id, na_peca) => mandar(`/api/provas/${id}/marcar`, { na_peca });

/* -------------------------------------------------------------- peças */

export const pecas = () => pegar("/api/pecas");
export const criarPeca = (record_id, tipo) => mandar("/api/pecas", { record_id, tipo });
/** Apaga a peça e as versões dela. O servidor recusa a que já foi protocolada. */
export const apagarPeca = (id) => mandar(`/api/pecas/${id}`, undefined, "DELETE");
export const gerarMinuta = (id) => mandar(`/api/pecas/${id}/minuta`, {});

/**
 * O .docx da peça: bytes na ida e na volta.
 *
 * O editor abre o arquivo que vai para o fórum — com timbre, rodapé e margens
 * do papel da casa —, então aqui não passa JSON: `no-store` porque a peça muda
 * a cada gravação, e o PUT manda o corpo cru que o servidor grava e versiona.
 */
export const documentoDaPeca = async (id) => {
  const caminho = `/api/pecas/${id}/documento`;
  const r = await fetch(caminho, {
    credentials: "same-origin",
    cache: "no-store",
    signal: comPrazo(undefined, caminho),
  }).catch((e) => semResposta(e, caminho));
  /* `ler` sempre lança quando não está ok: é por ele que a peça apagada chega
     ao editor como 404 com frase, e não como "Falha 404" em código. */
  if (!r.ok) await ler(r);
  return new Uint8Array(await r.arrayBuffer());
};

export const salvarDocumentoDaPeca = (id, bytes) =>
  fetch(`/api/pecas/${id}/documento`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/octet-stream", "x-crm-csrf": csrf() },
    body: bytes,
  }).then(ler);

/**
 * A minuta sendo montada, com o texto chegando aos pedaços.
 *
 * Resposta em fluxo, não o canal de eventos: é um pedido com um dono e um fim,
 * e o canal (`/api/stream`) é assinatura da mesa inteira. Devolve o que o
 * servidor mandou no fim — versão gravada, pendências e por qual via veio.
 */
export async function montarMinutaEmFluxo(id, { agente = null, aoPedaco, aoFase, sinal } = {}) {
  const r = await fetch(`/api/pecas/${id}/minuta/fluxo`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-crm-csrf": csrf() },
    body: JSON.stringify({ agente }),
    signal: sinal,
  });
  if (!r.ok || !r.body) throw new Error((await r.json().catch(() => null))?.erro ?? `Falha ${r.status}`);

  const leitor = r.body.getReader();
  const decodificador = new TextDecoder();
  let sobra = "";
  let fim = null;
  for (;;) {
    const { done, value } = await leitor.read();
    if (done) break;
    sobra += decodificador.decode(value, { stream: true });
    /* Só linha inteira é lida: um pedaço da rede corta o `data:` no meio. */
    const linhas = sobra.split("\n");
    sobra = linhas.pop() ?? "";
    for (const linha of linhas) {
      if (!linha.startsWith("data:")) continue;
      let dado;
      try {
        dado = JSON.parse(linha.slice(5).trim());
      } catch {
        continue;
      }
      if (dado.erro) throw new Error(dado.erro);
      if (dado.fase) aoFase?.(dado.fase);
      if (dado.pedaco) aoPedaco?.(dado.pedaco);
      if (dado.pronto) fim = dado;
    }
  }
  if (!fim) throw new Error("A montagem foi interrompida antes de terminar.");
  return fim;
}

/** A IA propõe trocas na peça. Nada é gravado: quem aplica é a tela, uma a uma. */
export const pedirEdicoesIA = (id, pedido, { trecho = null, sinal } = {}) =>
  fetch(`/api/pecas/${id}/ia`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-crm-csrf": csrf() },
    body: JSON.stringify({ pedido, trecho }),
    signal: sinal,
  }).then(ler);

/* ---------------------------------------------------- reuniões e docs */

export const gravacoes = (q) => pegar(`/api/gravacoes${q ? `?q=${encodeURIComponent(q)}` : ""}`);
/**
 * A gravação inteira, ACHATADA.
 *
 * A rota devolve `{ gravacao, itens, marcadores }`, e as duas telas que leem isto
 * tratavam a resposta como a própria gravação: `r.transcricao` e `r.audio_arquivo`
 * vinham `undefined`, então reunião gravada com áudio e texto no banco abria vazia
 * — "não salvou nada". Desembrulhar aqui, uma vez, em vez de em cada tela.
 */
export const gravacao = (id) =>
  pegar(`/api/gravacoes/${id}`).then((r) => ({ ...r.gravacao, itens: r.itens ?? [], marcadores: r.marcadores ?? [] }));
/** O áudio da reunião, servido pelo CRM (fica em data/gravacoes). */
export const audioDaGravacao = (id) => `/api/gravacoes/${id}/audio`;
/** O vídeo da aba, quando guardado. Mesma porta do áudio, com `Range`. */
export const videoDaGravacao = (id) => `/api/gravacoes/${id}/video`;
export const apagarVideoDaGravacao = (id) => mandar(`/api/gravacoes/${id}/video`, {}, "DELETE");
export const vincularGravacao = (id, record_id) => mandar(`/api/gravacoes/${id}/cliente`, { record_id }, "PATCH");
export const resumirGravacao = (id) => mandar(`/api/gravacoes/${id}/resumo`, {});
export const usarNoAlinhamento = (id) => mandar(`/api/gravacoes/${id}/alinhamento`, {});
export const alternarItemDaGravacao = (id, feito) => mandar(`/api/gravacoes/itens/${id}`, { feito }, "PATCH");
export const apagarGravacao = (id) => mandar(`/api/gravacoes/${id}`, {}, "DELETE");

/** A prévia ao vivo: um pedaço de áudio vira texto sem virar registro. */
export const transcreverPedaco = (arquivo) => {
  const form = new FormData();
  form.append("audio", arquivo, arquivo.name || "previa.webm");
  return enviarArquivo("/api/gravacoes/previa", form).then((r) => r.texto ?? "");
};

/**
 * Sobe a reunião inteira: as duas trilhas, a mistura, os quadros da tela e os
 * marcadores. É o servidor que transcreve, intercala e diariza.
 */
export const guardarReuniao = ({ mic, aba, audioMix, video, quadros, marcadores, duracaoSeg, titulo, recordId, config }) => {
  const form = new FormData();
  if (mic) form.append("audio_mic", mic, mic.name);
  if (aba) form.append("audio_aba", aba, aba.name);
  if (audioMix) form.append("audio_mix", audioMix, audioMix.name);
  if (video) form.append("video", video, video.name);
  form.append("duracao_seg", String(duracaoSeg ?? 0));
  form.append("titulo", titulo ?? "");
  form.append("idioma", config?.idioma ?? "pt");
  /*
   * Rótulo em branco NÃO é enviado: quem sabe o nome de quem conduziu é o
   * servidor (é a sessão), e mandar "Escritório" fixo daqui apagava esse nome em
   * toda fala da transcrição — o defeito que fazia a reunião inteira sair com
   * "**Escritório**" no lugar do advogado.
   */
  if (config?.rotuloMic) form.append("rotulo_mic", config.rotuloMic);
  if (config?.rotuloOutroLado) form.append("rotulo_aba", config.rotuloOutroLado);
  if (recordId) form.append("record_id", String(recordId));
  form.append("marcadores", JSON.stringify(marcadores ?? []));
  /* Os segundos vão num campo à parte, na MESMA ordem dos quadros: multipart não
     garante ordem entre campos, mas garante dentro do mesmo nome. */
  form.append("quadros_seg", JSON.stringify((quadros ?? []).map((q) => q.segundo)));
  for (const [i, q] of (quadros ?? []).entries()) form.append("quadros", q.blob, `quadro-${i}.jpg`);
  return enviarArquivo("/api/gravacoes", form);
};
/**
 * O acervo navegável — o "drive" do escritório.
 *
 * Um pedido só devolve as pastas com contagem, os arquivos do recorte pedido e
 * o total: a tela precisa dos três juntos para dizer "38 arquivos · 12 MB" sem
 * pedir de novo a cada filtro.
 */
export const acervo = (filtro = {}) => {
  const busca = new URLSearchParams(Object.entries(filtro).filter(([, v]) => v));
  return pegar(`/api/documentos/acervo${busca.toString() ? `?${busca}` : ""}`);
};

export const apagarDoAcervo = (pasta, nome) =>
  mandar(`/api/documentos/arquivo/${encodeURIComponent(pasta)}/${encodeURIComponent(nome)}`, {}, "DELETE");

/** Põe (ou tira) arquivos de uma pasta do cliente. Pasta vazia é tirar. */
export const organizarNoAcervo = (cliente, arquivos, pasta) => mandar("/api/documentos/organizar", { cliente, arquivos, pasta });
/** Cria a pasta vazia — o "Nova pasta" do drive. */
export const criarPastaNoAcervo = (cliente, pasta) => mandar("/api/documentos/pastas", { cliente, pasta });
/** Apaga a PASTA, não os arquivos: eles voltam para a raiz do cliente. */
export const apagarPastaDoAcervo = (cliente, pasta) =>
  mandar(`/api/documentos/pastas/${encodeURIComponent(cliente)}/${encodeURIComponent(pasta)}`, {}, "DELETE");
/** Renomeia a pasta inteira; nome vazio desfaz a organização dela. */
export const renomearPastaDoAcervo = (cliente, de, para) => mandar("/api/documentos/pasta", { cliente, de, para });
export const equipe = () => pegar("/api/people");

/* ------------------------------------------------- equipe e acessos */

/**
 * Pessoa e credencial na mesma resposta.
 *
 * A tela de equipe precisa das duas metades ao mesmo tempo — quem faz o quê no
 * fluxo e quem entra no sistema —, e o servidor já sabe casar as duas. Pedir
 * `/api/people` e `/api/usuarios` separado devolveria duas listas que a tela
 * teria de cruzar de novo, pelo nome, errando nos homônimos.
 */
/* `periodo` é o recorte do PLACAR de contratos, que vem na mesma ida da equipe:
   é a mesma tela e a mesma pergunta ("quem é o time, e como estamos"). */
export const equipeCompleta = (periodo = "") => pegar(`/api/equipe${consulta({ periodo })}`);
export const salvarPessoa = (pessoa) => mandar("/api/equipe", pessoa);
/** O que a saída desfaz (casos para a fila, tarefas sem dono, login que a impede). */
export const previaDaSaida = (id) => pegar(`/api/equipe/${id}/saida`);
/** Apaga a pessoa de vez: os casos dela voltam para a fila. */
export const excluirDaEquipe = (id) => mandar(`/api/equipe/${id}`, {}, "DELETE");

export const subirAvatar = (id, arquivo) => {
  const form = new FormData();
  form.append("arquivo", arquivo);
  return enviarArquivo(`/api/people/${id}/avatar`, form);
};

export const criarAcesso = (b) => mandar("/api/usuarios", b);
export const ajustarUsuario = (id, patch) => mandar(`/api/usuarios/${id}`, patch, "PATCH");
export const redefinirSenha = (id, senha) => mandar(`/api/usuarios/${id}/senha`, senha ? { senha } : {});

/** O retrato do acesso de uma pessoa: cada ação, e de onde ela vem. */
export const acessoDe = (id) => pegar(`/api/usuarios/${id}/acesso`);
export const definirAcesso = (id, permissao, estado) => mandar(`/api/usuarios/${id}/acesso`, { permissao, estado });
export const devolverAoPapel = (id) => mandar(`/api/usuarios/${id}/acesso`, { limpar: true });

export const papeis = () => pegar("/api/papeis");
export const criarPapel = (b) => mandar("/api/papeis", b);
export const ajustarPapel = (chave, patch) => mandar(`/api/papeis/${encodeURIComponent(chave)}`, patch, "PATCH");
export const apagarPapel = (chave) => mandar(`/api/papeis/${encodeURIComponent(chave)}`, {}, "DELETE");

/** Trilha de segurança: login, senha trocada, papel mudado. */
export const trilhaDeAcesso = (limite = 120) => pegar(`/api/usuarios/trilha?limite=${limite}`);
/* ------------------------------------------------- agente local (relay) */

/**
 * O agente que roda na máquina de quem atende.
 *
 * Devolve o estado do relay e os dois comandos de instalação já com o token
 * dentro — é a tela que os mostra para copiar, e é por isso que eles vêm do
 * servidor prontos em vez de montados aqui.
 */
export const relay = () => pegar("/api/acp/relay");
export const girarTokenDoRelay = () => mandar("/api/acp/relay/token", {});

/* ------------------------------------------------- assistentes (ACP) */

/*
 * A página de assistentes fala com o MESMO conjunto de rotas que o CRM já
 * expunha (`src/rotas/agentes.ts`) — nada aqui é regra, é só o caminho.
 *
 * Duas listas e não uma: `assistentes()` traz os agentes configurados E os
 * pedidos de permissão pendentes na mesma ida, porque a tela desenha os dois
 * juntos (o cabeçalho do assistente e o aviso "ele está esperando você"). A
 * conversa é buscada à parte porque ela é o que muda o tempo todo.
 */
export const assistentes = () => pegar("/api/agents");
export const ligarAssistente = (id) => mandar(`/api/agents/${encodeURIComponent(id)}/start`, {});
export const desligarAssistente = (id) => mandar(`/api/agents/${encodeURIComponent(id)}/stop`, {});

/** As conversas de TODOS os assistentes — é o que a lateral abre. */
export const conversasDosAssistentes = (arquivadas = false) => pegar(`/api/conversas?arquivadas=${arquivadas ? 1 : 0}`);
export const conversaDoAssistente = (cid) => pegar(`/api/conversas/${encodeURIComponent(cid)}`);
export const criarConversaDoAssistente = (id, titulo) =>
  mandar(`/api/agents/${encodeURIComponent(id)}/conversas`, titulo ? { titulo } : {});
/** Sobe o processo e cria (ou retoma) a sessão ACP daquela conversa. */
export const abrirConversaDoAssistente = (cid) => mandar(`/api/conversas/${encodeURIComponent(cid)}/abrir`, {});
export const ajustarConversaDoAssistente = (cid, mudanca) =>
  mandar(`/api/conversas/${encodeURIComponent(cid)}`, mudanca, "PATCH");
export const apagarConversaDoAssistente = (cid) => mandar(`/api/conversas/${encodeURIComponent(cid)}`, {}, "DELETE");

/**
 * A pergunta, com os arquivos anexados.
 *
 * Vai como `multipart` SEMPRE que houver arquivo, e como JSON quando não houver
 * — é o que a rota espera, e mandar `FormData` vazio custaria um corpo maior
 * por nada. O anexo é gravado pelo servidor na pasta do escritório corrente.
 */
export function perguntarAoAssistente(id, conversa, texto, arquivos = []) {
  const caminho = `/api/agents/${encodeURIComponent(id)}/prompt`;
  if (!arquivos.length) return mandar(caminho, { conversa, texto });
  const form = new FormData();
  form.append("conversa", conversa);
  form.append("texto", texto);
  for (const arquivo of arquivos) form.append("arquivo", arquivo);
  return enviarArquivo(caminho, form);
}

export const pararAssistente = (id, conversa) => mandar(`/api/agents/${encodeURIComponent(id)}/cancel`, { conversa });
/** Troca o modelo da conversa quando o agente publica uma lista (ACP `session/set_model`). */
export const modeloDoAssistente = (id, conversa, modelId) =>
  mandar(`/api/agents/${encodeURIComponent(id)}/model`, { conversa, modelId });
export const modoDoAssistente = (id, conversa, modeId) =>
  mandar(`/api/agents/${encodeURIComponent(id)}/mode`, { conversa, modeId });
/** Ajuste publicado pelo próprio agente (modelo, esforço, modo rápido…). */
export const opcaoDoAssistente = (id, conversa, configId, value) =>
  mandar(`/api/agents/${encodeURIComponent(id)}/config`, { conversa, configId, value });
export const autenticarAssistente = (id, methodId) => mandar(`/api/agents/${encodeURIComponent(id)}/auth`, { methodId });
export const ajustesDoAssistente = (id, ajustes) => mandar(`/api/agents/${encodeURIComponent(id)}/settings`, ajustes);

/** A resposta ao pedido de autorização. Escreve no CRM: nunca sai daqui sozinha. */
export const responderPermissaoDoAssistente = (id, optionId) =>
  mandar(`/api/permissions/${encodeURIComponent(id)}`, { optionId });

/* O sino pede doze (o padrão do servidor); a tela de Notificações pede tudo. */
/* O teto do servidor. A página pede tudo; o sino usa o padrão de doze. */
export const TETO_DE_AVISOS = 200;
export const avisos = (limite) => pegar(`/api/avisos${limite ? `?limite=${limite}` : ""}`);
/** "Já vi": as chaves somem do sino desta pessoa até o fato mudar. */
export const marcarAvisosLidos = (chaves) => mandar("/api/avisos/lidos", { chaves });
export const ajustes = () => pegar("/api/ajustes");

/* --------------------------------------------------- carteira de contatos */

/** Quantos contatos por página. O servidor tem o mesmo teto. */
export const TAMANHO_DA_CARTEIRA = 60;

/**
 * A carteira de contatos: lista, painel, pastilhas e facetas numa ida só.
 *
 * `/api/carteira` e não `/api/contatos`: aquele endereço já é a agenda do
 * aparelho, e duas rotas com o mesmo prefixo respondendo coisas diferentes é
 * como a agenda voltou vazia com treze prazos no banco.
 *
 * Vem por `pegarComTotal` porque a lista é paginada e o pedaço sozinho não diz
 * se acabou — é o `x-crm-total` que faz a tela escrever "60 de 528".
 */
export const carteira = (recorte = {}, sinal) => pegarComTotal(`/api/carteira${consulta(recorte)}`, sinal);

/**
 * O endereço do arquivo — a tela navega até ele, não busca por `fetch`.
 *
 * Download começado por `fetch` teria de virar `blob:` e um `<a download>` de
 * mentira; o navegador já sabe salvar o que vem com `content-disposition`, e
 * assim o arquivo grande não passa pela memória da aba.
 */
export const enderecoDaExportacao = (recorte = {}, formato = "xlsx") =>
  `/api/carteira/exportar${consulta({ ...recorte, formato })}`;

/* --------------------------------------------- publicações dos diários (DJEN) */

/** Quantas publicações por página. O servidor tem o mesmo teto. */
export const TAMANHO_DAS_PUBLICACOES = 60;

/**
 * A tela de Publicações numa ida: lista, contadores, facetas e quem é vigiado.
 *
 * Vem por `pegarComTotal` porque a lista é paginada e o pedaço sozinho não diz
 * se acabou — é o `x-crm-total` que faz a tela escrever "60 de 1.984".
 */
export const publicacoes = (recorte = {}, sinal) => pegarComTotal(`/api/publicacoes${consulta(recorte)}`, sinal);

/* Buscar no diário AGORA. Sem intervalo, o servidor usa ontem→hoje, que é a
   pergunta do dia ("o que saiu desde a última vez que olhei"). */
export const varrerPublicacoes = (intervalo = {}) => mandar("/api/publicacoes/varrer", intervalo);
export const tratarPublicacao = (id, mudanca) => mandar(`/api/publicacoes/${id}`, mudanca, "PATCH");
/* O prazo aberto a partir da publicação: a DATA vem da tela, sempre — contar
   prazo é julgamento de advogado, e o texto só sugere o número de dias. */
export const abrirPrazoDaPublicacao = (id, prazo) => mandar(`/api/publicacoes/${id}/prazo`, prazo);

/* O endereço do arquivo — a tela navega até ele, como na carteira: o navegador
   já sabe salvar o que vem com `content-disposition`. */
export const enderecoDasPublicacoes = (recorte = {}) => `/api/publicacoes/exportar${consulta(recorte)}`;

/* ------------------------------------------------- tarefas, agenda e rápidas */

/**
 * O quadro de tarefas do escritório.
 *
 * Uma ida só: o quadro, os contadores de cada coluna, a carga por pessoa e os
 * prazos da semana saem da mesma consulta. Contar na tela contaria a página
 * carregada — o mesmo erro que a lista da mesa já cometeu uma vez.
 */
export const tarefas = (filtros = {}) => pegar(`/api/tarefas${consulta(filtros)}`);
export const criarTarefa = (tarefa) => mandar("/api/tarefas", tarefa);
export const ajustarTarefa = (id, mudanca) => mandar(`/api/tarefas/${id}`, mudanca, "PATCH");
/* Arrastar no quadro: coluna de destino e o índice DENTRO dela (0 = topo). Quem
   calcula a posição é o servidor — a tela manda o índice que desenhou. */
export const moverTarefaNoQuadro = (id, { estado, indice }) => mandar(`/api/tarefas/${id}/mover`, { estado, indice });
/* Concluir é rota própria, e não `PATCH estado=concluido`: fechar tarefa grava
   `concluida`, `concluida_em` e o resultado na mesma transação. */
export const concluirTarefa = (id, resultado) => mandar(`/api/tarefas/${id}/concluir`, { resultado });
export const apagarTarefa = (id) => mandar(`/api/tarefas/${id}`, {}, "DELETE");

/**
 * A agenda: prazo de tarefa, reunião marcada e mensagem com hora marcada, no
 * mesmo eixo do tempo. É leitura — não existe tabela de "compromisso".
 */
/* `/api/compromissos` e não `/api/agenda`: a segunda é o diário do relógio,
   que existe desde antes (ver o comentário em `rotas/compromissos.ts`). */
export const agenda = (de, ate) => pegar(`/api/compromissos${consulta({ de, ate })}`);
/* Cancelar o envio marcado NÃO ganha rota nova: `cancelarAgendada` acima já
   faz isso pela rota de leads, que é onde o agendamento nasce. A agenda só
   precisa carregar o `record_id` junto do item para poder chamar. */

/** As respostas prontas do escritório, com categoria e contador de uso. */
/* Criativos: a tela inteira numa ida, e o cadastro dos códigos do anúncio. */
export const criativos = ({ de, ate, numero } = {}, sinal) => pegar(`/api/criativos${consulta({ de, ate, numero })}`, sinal);
export const cadastrarCodigo = (dados) => mandar("/api/criativos/codigos", dados);
export const editarCodigo = (id, dados) => mandar(`/api/criativos/codigos/${id}`, dados, "PATCH");
export const ignorarCodigo = (codigo) => mandar(`/api/criativos/vistos/${encodeURIComponent(codigo)}/ignorar`, {});

export const rapidas = () => pegar("/api/rapidas");
export const salvarRapida = (id, dados) => (id ? mandar(`/api/rapidas/${id}`, dados, "PATCH") : mandar("/api/rapidas", dados));
export const apagarRapida = (id) => mandar(`/api/rapidas/${id}`, {}, "DELETE");
/* Usar conta o uso E resolve as variáveis com o caso: a tela nunca substitui
   `{nome}` por conta própria — quem sabe o nome do cliente é o servidor. */
/* ------------------------------------------------- reunião de alinhamento */

/**
 * Abrir, marcar, avisar e apagar a reunião do caso.
 *
 * A tabela existia desde sempre e a tela não a lia: a fila que a confirmação de
 * tese abre sozinha, a data marcada e a sala da chamada viviam só no banco. A
 * mesa mostrava as GRAVAÇÕES (o que já aconteceu) e não o que vai acontecer.
 */
export const abrirReuniao = (record_id) => mandar("/api/reunioes", { record_id });
export const marcarReuniao = (id, quando, participantes = "") => mandar(`/api/reunioes/${id}/marcar`, { quando, participantes });
/** Manda ao cliente a data e o link da sala. Permissão de falar com o cliente. */
export const avisarDaReuniao = (id) => mandar(`/api/reunioes/${id}/avisar`, {});
export const apagarReuniao = (id) => mandar(`/api/reunioes/${id}`, {}, "DELETE");

export const usarRapida = (id, record_id) => mandar(`/api/rapidas/${id}/usar`, { record_id });

/* --------------------------------------- o arquivo que sai com a pronta */

/**
 * O anexo da resposta pronta, como File — pronto para prender no compositor.
 *
 * Vem por `fetch` e não por `<a href>` porque o destino é o compositor: quem
 * insere `/procuracao` tem de ver o cartão do arquivo ao lado do texto, conferir
 * e mandar os dois num Enter. Baixar para depois anexar à mão é o passo em que a
 * pronta com arquivo deixa de ser usada.
 */
export async function anexoDaRapida(id, nome) {
  const r = await fetch(`/api/rapidas/${id}/anexo`, { credentials: "same-origin" });
  if (!r.ok) throw new Error("O arquivo desta resposta pronta não veio.");
  const bloco = await r.blob();
  return new File([bloco], nome || `rapida-${id}`, { type: bloco.type || "application/octet-stream" });
}

/** Sobe (ou troca) o arquivo da pronta. `desanexarRapida` o tira. */
export const anexarNaRapida = (id, arquivo) => {
  const form = new FormData();
  form.append("arquivo", arquivo);
  return enviarArquivo(`/api/rapidas/${id}/anexo`, form);
};
export const desanexarRapida = (id) => mandar(`/api/rapidas/${id}/anexo`, {}, "DELETE");
/* Agentes de IA: os sete de fábrica, com gatilhos e o que fazem. */
export const agentesIa = () => pegar("/api/agentes-ia");
export const salvarAgenteIa = (id, dados) => mandar(`/api/agentes-ia/${id}`, dados, "PATCH");
export const ligarAgenteIa = (id, ativo) => mandar(`/api/agentes-ia/${id}/ativo`, { ativo });
export const agenteIaAoPadrao = (id) => mandar(`/api/agentes-ia/${id}/padrao`, {});
/* O ensaio num caso de verdade: passos e o texto — nada sai. */
export const ensaiarAgenteIa = (id, record_id) => mandar(`/api/agentes-ia/${id}/ensaiar`, { record_id });

/* O pedido feito de dentro da conversa do cliente: manda o trecho citado ao
   assistente e devolve a conversa dele, para a tela levar quem pediu até lá. */
export const pedirAoAssistente = (corpo) => mandar("/api/agentes/pedido", corpo);

/* ------------------------------------------------------------ etiquetas */

/* O recorte que o escritório inventa. A etiqueta nasce escrita — não há
   cadastro: `porEtiqueta` cria se ainda não existir. */
export const porEtiqueta = (id, tag) => mandar(`/api/leads/${id}/tags`, { tag });
export const tirarEtiqueta = (id, tag) => mandar(`/api/leads/${id}/tags/${encodeURIComponent(tag)}`, {}, "DELETE");
/** Renomear e apagar valem para o escritório inteiro — é o mesmo rótulo em todos. */
export const renomearEtiqueta = (tag, novo) => mandar(`/api/tags/${encodeURIComponent(tag)}`, { tag: novo }, "PATCH");
export const apagarEtiqueta = (tag) => mandar(`/api/tags/${encodeURIComponent(tag)}`, {}, "DELETE");
/** A cor do RÓTULO, para todos os casos. `""` devolve a cor automática. */
export const pintarEtiqueta = (tag, cor) => mandar(`/api/tags/${encodeURIComponent(tag)}`, { cor }, "PATCH");

export const ajustar = (chave, valor) => mandar("/api/ajustes", { chave, valor });
/** O catálogo de modelos do provedor (OpenRouter), para o seletor de Ajustes. */
export const modelosDeIA = () => pegar("/api/ia/modelos");
export const schema = () => pegar("/api/schema");
/**
 * Campo NOVO pelo rótulo que a pessoa escreveu — a porta da ficha do caso.
 *
 * `/api/campos` e não `/api/fields`: o segundo pede a `chave` pronta (serve ao
 * importador e ao MCP), e quem está olhando o caso não tem de inventar
 * `data_da_demissao`. Quem deriva a chave é o domínio, pela mesma regra da
 * planilha — duas regras seriam dois campos para o mesmo dado.
 */
export const criarCampo = ({ rotulo, tipo, lista }) => mandar("/api/campos", { rotulo, tipo, lista });

/*
 * O ESQUEMA do funil, escrito pela tela.
 *
 * As três rotas existiam no servidor (e no MCP) desde sempre e nenhuma tela as
 * chamava: o playbook de cada etapa — o critério de entrada e o objetivo que a
 * IA persegue — e a lista de documentos de cada tese só se mudavam pedindo ao
 * agente ou no banco. A Central da IA é quem passou a usá-las.
 */
export const salvarEtapa = (etapa) => mandar("/api/stages", etapa);
export const incluirNaLista = (lista, valor, ordem) => mandar("/api/lists", { lista, valor, ordem });
export const tirarDaLista = (lista, valor) =>
  mandar(`/api/lists?lista=${encodeURIComponent(lista)}&valor=${encodeURIComponent(valor)}`, {}, "DELETE");

/** O diagnóstico do que está ligado — a tela de ajustes lê daqui. */
export const integracoes = () => pegar("/api/integracoes");

/** O retrato de desempenho: p50/p95 por rota, memória e tempo no ar. */
export const metricas = () => pegar("/api/metricas");

/* ------------------------------------------------------------ WhatsApp */

/*
 * A conexão do aparelho.
 *
 * CONFIGURAR o WhatsApp: qual provedor, com que credencial, o QR do pareamento.
 * O que cada provedor sabe fazer vem em `capacidades` — é assim que a tela apaga
 * o botão antes de alguém clicar e tomar um erro que não explica nada.
 */
export const conexaoWhatsapp = () => pegar("/api/whatsapp/conexao");
export const definirConexaoWhatsapp = (provedor, credenciais = {}) => mandar("/api/whatsapp/conexao", { provedor, credenciais });
/** `historico` é a resposta do "baixar as conversas antigas do celular?". */
export const conectarWhatsapp = (historico) => mandar("/api/whatsapp/conectar", historico === undefined ? {} : { historico });
export const qrWhatsapp = () => pegar("/api/whatsapp/qr");
export const desconectarWhatsapp = (desparear = false) => mandar("/api/whatsapp/desconectar", { desparear });
export const testarWhatsapp = (telefone, texto) => mandar("/api/whatsapp/testar", { telefone, texto });
export const sincronizarWhatsapp = (limite) => mandar("/api/whatsapp/sincronizar", { limite });

/* A caixa do aparelho e a agenda de contatos saíram com a tela delas: o CRM
   ainda serve `/api/whatsapp/conversas` e `/api/contatos`, e é de lá que a
   próxima tela que precisar deles vai ler. */
export const pedirHistorico = () => mandar("/api/whatsapp/historico", {});
/** A foto de perfil é servida pelo CRM: a URL do CDN do WhatsApp expira. */
export const avatarDoJid = (jid) => `/api/whatsapp/avatar/${encodeURIComponent(jid)}`;
/** A foto do caso do CRM: o servidor acha o jid (que pode ser um LID). */
export const avatarDoRegistro = (id) => `/api/whatsapp/avatar/registro/${id}`;

/* -------------------------------------------------- ações de uma mensagem */

export const reagir = (id, mid, emoji) => mandar(`/api/leads/${id}/mensagem/${mid}/reagir`, { emoji });
export const editarMensagem = (id, mid, texto) => mandar(`/api/leads/${id}/mensagem/${mid}/editar`, { texto });
export const apagarMensagem = (id, mid) => mandar(`/api/leads/${id}/mensagem/${mid}/apagar`, {});
/* Manda de novo o que ficou em ensaio (gravado no CRM e nunca enviado). */
export const reenviarEnsaio = (id, mid) => mandar(`/api/leads/${id}/mensagem/${mid}/reenviar`, {});
/** Lê por máquina o áudio ou a imagem desta bolha, para o que ficou sem texto. */
/** A mídia do histórico, baixada quando a bolha aparece (`midia_pendente`). */
export const baixarMidia = (id, mid) => mandar(`/api/leads/${id}/mensagem/${mid}/midia`, {});
export const transcreverMensagem = (id, mid) => mandar(`/api/leads/${id}/mensagem/${mid}/transcrever`, {});
/** A IA assume a conversa para buscar o que falta — a pergunta vai para a fila. */
export const pedirOQueFalta = (id) => mandar(`/api/copiloto/${id}/falta`, {});

/* ------------------------------------------------------------- chat da peça */
/** Histórico, catálogo de comandos e o que cada comando pode agora. */
export const chatDaPeca = (pecaId) => pegar(`/api/pecas/${pecaId}/chat`);
/** Um pedido no chat: texto livre, comando do catálogo e o trecho marcado. */
export const pedirNoChat = (pecaId, pedido) => mandar(`/api/pecas/${pecaId}/chat`, pedido);
/** Anexo do chat: o servidor guarda, lê (áudio e imagem) e devolve a bolha. */
export const anexarNoChat = (pecaId, form) => enviarArquivo(`/api/pecas/${pecaId}/chat/anexo`, form);
export const encaminhar = (id, mensagens, para) => mandar(`/api/leads/${id}/encaminhar`, { mensagens, para });
export const marcarLidoNoAparelho = (id) => mandar(`/api/leads/${id}/lido`, {});
/** Chamado a cada tecla: falha em silêncio de propósito (provedor sem presença). */
export const digitando = (id, parou = false, como = "digitando") =>
  mandar(`/api/leads/${id}/digitando`, { parou, como }).catch(() => null);

/* -------------------------------------------------- modelos de contrato */

export const modelosDeContrato = () => pegar("/api/contratos/modelos");
export const salvarModeloDeContrato = (id, dados) =>
  id ? mandar(`/api/contratos/modelos/${id}`, dados, "PATCH") : mandar("/api/contratos/modelos", dados);
export const apagarModeloDeContrato = (id) => mandar(`/api/contratos/modelos/${id}`, {}, "DELETE");
export const previaDoContrato = (textos, record_id) => mandar("/api/contratos/previa", { textos, record_id });
/** Lê o .docx da ZapSign e devolve os três textos — não grava. */
export const importarContratoDocx = (arquivo) => {
  const form = new FormData();
  form.append("arquivo", arquivo);
  return enviarArquivo("/api/contratos/importar", form);
};

/* ------------------------------------------------ conferência entre casos */

/** Os documentos que CHEGARAM e ninguém disse se servem, de todos os casos — a fila de conferir. */
export const filaAConferir = () => pegar("/api/checklist?a_conferir=1");

/* ---------------------------------------------------------- modo da IA */

/** O modo do escritório (`sem_ia`, `copiloto`, `piloto`) e o catálogo com rótulos. */
export const modoDaIA = () => pegar("/api/modo");
export const definirModoDaIA = (modo) => mandar("/api/modo", { modo }, "PUT");
/** O modo de UM caso; `null` volta a herdar o do escritório. */
export const definirModoDoCaso = (id, modo, agente_id = null) =>
  mandar(`/api/leads/${id}/modo`, { modo, ...(agente_id ? { agente_id } : {}) }, "PUT");
export const recepcaoAMao = (id, agente_id) => mandar(`/api/leads/${id}/recepcao`, { agente_id });

/* ------------------------------------------------------------- setores */

/*
 * A tela de Setores fala com `/api/workspaces/:chave/...` — cada ação leva o
 * setor na URL, então mexer no número do Previdenciário não troca a aba de
 * setor (trocar recarrega a mesa inteira, ver `trocarWorkspace`).
 */
const doSetor = (chave) => `/api/workspaces/${encodeURIComponent(chave)}`;
export const panoramaDosSetores = () => pegar("/api/workspaces/consolidado");
export const criarNumero = (chave, b) => mandar(`${doSetor(chave)}/conexoes`, b);
export const ajustarNumero = (chave, id, b) => mandar(`${doSetor(chave)}/conexoes/${id}`, b, "PATCH");
export const parearNumero = (chave, id) => mandar(`${doSetor(chave)}/conexoes/${id}/conectar`, {});
export const qrDoNumero = (chave, id) => pegar(`${doSetor(chave)}/conexoes/${id}/qr`);
export const tirarNumero = (chave, id) => mandar(`${doSetor(chave)}/conexoes/${id}`, {}, "DELETE");
export const destinosDeEncaminhar = () => pegar("/api/workspaces/destinos");
export const encaminharCaso = (record_id, destino) => mandar("/api/workspaces/encaminhar", { record_id, destino });

/* A tela Sistema (só o dev): saúde, motor e trilha numa ida. */
export const sistema = () => pegar("/api/sistema");
